import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { ThemeProvider, createGlobalStyle } from 'styled-components';
import {
  ACCENT_STORAGE_KEY,
  applyChromeAccent,
  applyRadius,
  ACCENT_OPTIONS,
  isRadiusId,
  RADIUS_STORAGE_KEY,
  THEME_STORAGE_KEY,
  themesByName,
} from './palettes';
import { useDisplayTimezone } from '../service/localTime';

const ThemeModeContext = createContext({
  mode: 'light',
  setMode: () => {},
  accentId: 'mint',
  setAccentId: () => {},
  radiusId: 'full',
  setRadiusId: () => {},
});

const isThemeName = (value) => Boolean(themesByName[value]);

const isAccentId = (value) =>
  ACCENT_OPTIONS.some((option) => option.id === value);

const readStoredAccent = () => {
  try {
    const stored = localStorage.getItem(ACCENT_STORAGE_KEY);
    if (isAccentId(stored)) return stored;
  } catch (error) {
    return 'mint';
  }
  return 'mint';
};

const readStoredMode = () => {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (isThemeName(stored)) return stored;
  } catch (error) {
    return 'light';
  }
  return 'light';
};

const readStoredRadius = () => {
  try {
    const stored = localStorage.getItem(RADIUS_STORAGE_KEY);
    if (isRadiusId(stored)) return stored;
  } catch (error) {
    return 'full';
  }
  return 'full';
};

const GlobalThemeStyle = createGlobalStyle`
  html {
    color-scheme: ${({ theme }) => (theme.isDark ? 'dark' : 'light')};
    font-synthesis: none;
    --tol-font-display: 'ClashTOL-Semibold', sans-serif;
    --tol-display-weight: 600;
    --tol-font-ui: 'Archivo', sans-serif;
    --tol-font-condensed: 'Archivo Condensed', 'Archivo', sans-serif;
    --tol-rail: ${({ theme }) => theme.rail};
    --tol-nav: ${({ theme }) => theme.nav};
    --tol-page: ${({ theme }) => theme.page};
    --tol-card: ${({ theme }) => theme.card};
    --tol-inset: ${({ theme }) => theme.inset};
    --tol-hover: ${({ theme }) => theme.hover};
    --tol-surface: ${({ theme }) => theme.surface};
    --tol-surface-alt: ${({ theme }) => theme.surfaceAlt};
    --tol-surface-hover: ${({ theme }) => theme.surfaceHover};
    --tol-text: ${({ theme }) => theme.text};
    --tol-text-muted: ${({ theme }) => theme.textMuted};
    --tol-text-faint: ${({ theme }) => theme.textFaint};
    --tol-chrome: ${({ theme }) => theme.chrome};
    --tol-sidebar: ${({ theme }) => theme.sidebar};
    --tol-border: ${({ theme }) => theme.border};
    --tol-border-subtle: ${({ theme }) => theme.borderSubtle};
    --tol-accent: ${({ theme }) => theme.accent};
    --tol-accent-soft: ${({ theme }) => theme.accentSoft};
    --tol-accent-contrast: ${({ theme }) => theme.accentContrast};
    --tol-success: ${({ theme }) => theme.success};
    --tol-header: ${({ theme }) => theme.header};
    --tol-header-accent: ${({ theme }) => theme.headerAccent};
    --tol-header-text: ${({ theme }) => theme.headerText};
    --tol-header-height: ${({ theme }) => theme.headerHeight};
    --tol-chrome-radius: ${({ theme }) => theme.chromeRadius};
    --tol-card-radius: ${({ theme }) => theme.cardRadius};
    --tol-control-radius: ${({ theme }) => theme.controlRadius};
    --tol-skeleton: ${({ theme }) => theme.skeleton};
    --tol-skeleton-shine: ${({ theme }) => theme.skeletonShine};
  }

  #root {
    min-height: 100vh;
    background: ${({ theme }) => theme.page};
    color: ${({ theme }) => theme.text};
    font-family: var(--tol-font-ui);
  }

  body {
    font-family: var(--tol-font-ui);
  }

  /* Zero-specificity so Clash titles (h2) are not stuck on Archivo Bold from the old sheet. */
  :where(h1, h2, h3, h4, h5, h6) {
    font-synthesis: none;
  }

  * {
    scrollbar-width: none;
    -ms-overflow-style: none;
  }
  *::-webkit-scrollbar {
    display: none;
    width: 0;
    height: 0;
  }

  svg path[stroke='#273D54'] {
    stroke: ${({ theme }) => theme.textFaint};
  }

  .__react_component_tooltip {
    background: ${({ theme }) => theme.card} !important;
    color: ${({ theme }) => theme.text} !important;
    border: 1px solid ${({ theme }) => theme.border} !important;
    overflow: visible !important;
    opacity: 1 !important;
    border-radius: 6px !important;
    font-family: var(--tol-font-ui) !important;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35) !important;
    white-space: normal !important;
    width: max-content !important;
    max-width: 240px !important;
    box-sizing: border-box !important;
  }
  .__react_component_tooltip.type-light,
  .__react_component_tooltip.type-dark,
  .__react_component_tooltip.type-custom {
    background: ${({ theme }) => theme.card} !important;
    color: ${({ theme }) => theme.text} !important;
  }
  .__react_component_tooltip::before {
    display: none !important;
    content: none !important;
    border: 0 !important;
  }
  .__react_component_tooltip::after {
    content: '' !important;
    display: block !important;
    position: absolute !important;
    width: 8px !important;
    height: 8px !important;
    box-sizing: border-box !important;
    background: var(--tol-card) !important;
    border: 1px solid var(--tol-border) !important;
    margin: 0 !important;
    box-shadow: none !important;
  }
  .__react_component_tooltip.place-bottom::after {
    top: -5px !important;
    bottom: auto !important;
    left: 50% !important;
    right: auto !important;
    border-right: 0 !important;
    border-bottom: 0 !important;
    transform: translateX(-50%) rotate(45deg) !important;
  }
  .__react_component_tooltip.place-top::after {
    top: auto !important;
    bottom: -5px !important;
    left: 50% !important;
    right: auto !important;
    border-top: 0 !important;
    border-left: 0 !important;
    transform: translateX(-50%) rotate(45deg) !important;
  }
  .__react_component_tooltip.place-left::after {
    top: 50% !important;
    bottom: auto !important;
    left: auto !important;
    right: -5px !important;
    border-bottom: 0 !important;
    border-left: 0 !important;
    transform: translateY(-50%) rotate(45deg) !important;
  }
  .__react_component_tooltip.place-right::after {
    top: 50% !important;
    bottom: auto !important;
    left: -5px !important;
    right: auto !important;
    border-top: 0 !important;
    border-right: 0 !important;
    transform: translateY(-50%) rotate(45deg) !important;
  }
`;

export const useThemeMode = () => useContext(ThemeModeContext);

export const ThemeRoot = ({ children }) => {
  const [mode, setModeState] = useState(readStoredMode);
  const [accentId, setAccentState] = useState(readStoredAccent);
  const [radiusId, setRadiusState] = useState(readStoredRadius);
  const displayTimezone = useDisplayTimezone();

  const setMode = (next) => {
    if (isThemeName(next)) setModeState(next);
  };

  const setAccentId = (next) => {
    if (isAccentId(next)) setAccentState(next);
  };

  const setRadiusId = (next) => {
    if (isRadiusId(next)) setRadiusState(next);
  };

  useEffect(() => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, mode);
    } catch (error) {
      /* ignore quota / private mode */
    }
    document.documentElement.setAttribute('data-theme', mode);
  }, [mode]);

  useEffect(() => {
    try {
      localStorage.setItem(ACCENT_STORAGE_KEY, accentId);
    } catch (error) {
      /* ignore quota / private mode */
    }
    document.documentElement.setAttribute('data-accent', accentId);
  }, [accentId]);

  useEffect(() => {
    try {
      localStorage.setItem(RADIUS_STORAGE_KEY, radiusId);
    } catch (error) {
      /* ignore quota / private mode */
    }
    document.documentElement.setAttribute('data-radius', radiusId);
  }, [radiusId]);

  const value = useMemo(
    () => ({
      mode,
      setMode,
      accentId,
      setAccentId,
      radiusId,
      setRadiusId,
    }),
    [mode, accentId, radiusId]
  );

  const theme = {
    ...applyRadius(
      applyChromeAccent(themesByName[mode] || themesByName.light, accentId),
      radiusId
    ),
    displayTimezone,
  };

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <GlobalThemeStyle />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
};

export default ThemeRoot;
